import { AnimatePresence, motion } from "framer-motion";
import { useHistory } from "react-router-dom";

import { Button } from "components/DesignSystem/Button/Button";
import { CrossIcon } from "components/icons/CrossIcon";
import { Passkey } from "components/icons/Passkey";
import {
  PASSKEY_PROMPT_DISMISSED,
  PASSKEY_PROMPT_SETUP_CLICKED,
} from "constants/analyticsEvents";
import { useAnalytics } from "hooks/useAnalytics";

type PasskeyPromptCardProps = {
  open: boolean;
  onDismiss: () => void;
};

export const PasskeyPromptCard = ({
  open,
  onDismiss,
}: PasskeyPromptCardProps) => {
  const history = useHistory();
  const { trackEvent } = useAnalytics();

  const handleGoToSettings = () => {
    trackEvent(PASSKEY_PROMPT_SETUP_CLICKED);
    onDismiss();
    history.push("/settings/profile#passkeys");
  };

  const handleDismiss = () => {
    trackEvent(PASSKEY_PROMPT_DISMISSED);
    onDismiss();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className="t-fixed t-bottom-6 t-right-6 t-z-toast t-w-[360px]"
        >
          <div className="t-shadow-light-60 t-flex t-gap-3 t-rounded-lg t-border t-border-solid t-border-neutral-10 t-bg-surface t-p-4">
            <div className="t-flex t-size-9 t-shrink-0 t-items-center t-justify-center t-rounded-full t-bg-purple-0 t-text-purple">
              <Passkey width={18} height={18} />
            </div>
            <div className="t-flex t-flex-col t-gap-2">
              <div className="t-flex t-min-h-9 t-items-center t-justify-between t-gap-2">
                <div className="t-text-body t-font-medium t-text-text-100">
                  Sign in faster next time
                </div>
                <Button
                  type="button"
                  customType="ghost_icon"
                  size="xsmall"
                  onClick={handleDismiss}
                  aria-label="Dismiss"
                >
                  <CrossIcon width={14} height={14} />
                </Button>
              </div>
              <div className="t-text-body-sm t-text-text-30">
                Add a passkey to sign in with just your face or fingerprint.
              </div>
              <div className="t-mt-1 t-flex t-items-center t-gap-4">
                <Button
                  customType="primary"
                  size="small"
                  type="button"
                  onClick={handleGoToSettings}
                >
                  Set up passkey
                </Button>
                <Button
                  customType="text"
                  size="small"
                  type="button"
                  onClick={handleDismiss}
                >
                  Not now
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
